import React from 'react';
import moment from 'moment';
import { Box, Chip, Divider, Typography } from '@mui/material';
import { ThemeContext } from 'src/store/ContextStore';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import axios from 'src/axios';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import { CLINICAL_COLOURS } from 'src/theme/palette';
import { useAutoTranslation, translate } from 'src/hooks/useTranslate';
import { english } from 'src/i18n/language';
import PackResult, { fromStored } from './triage/PackResult';
import { outcomeText } from './triage/triageText';
import { toLocalDate } from 'src/util/partialDate';
import {
  MEASUREMENT_LABELS,
  bmiOf,
  measurementSummary,
  smokingText
} from './qrisk3/measurementsText';
import { MODEL_NAME, qrisk3Summary } from './qrisk3/qrisk3Text';
import Qrisk3StoredResult from './qrisk3/Qrisk3StoredResult';

// Every risk model is drawn in the Risk Score record colour, and so is a
// stored triage result (TGCLOUD-1910), which is kept beside them.
const TYPE_COLOURS = {
  gail: CLINICAL_COLOURS.riskScore,
  canrisk: CLINICAL_COLOURS.riskScore,
  prs: CLINICAL_COLOURS.riskScore,
  triage: CLINICAL_COLOURS.riskScore,
  measurements: CLINICAL_COLOURS.riskScore,
  qrisk3: CLINICAL_COLOURS.riskScore
};

// The models' own names, shown as they are. 'triage' is a word, so it is
// translated where it is shown (typeLabel).
const TYPE_LABELS = {
  gail: 'GAIL',
  canrisk: 'CanRisk',
  prs: 'PRS',
  qrisk3: MODEL_NAME
};

// A triage record is named by its rule pack ("NICE CG164"), which is the pack's
// own name and shown as it is.
const typeLabel = (record, t = english) => {
  if (record?.recordType === 'triage')
    return (
      record.triageResult?.shortName ||
      record.triageResult?.packName ||
      t('Triage')
    );
  if (record?.recordType === 'measurements') return t('Clinical measurements');
  return TYPE_LABELS[record?.recordType] || record?.recordType;
};

// A list's own name, for the collapsed-row summary (read when reported).
const listLabel = (recordType) => {
  if (recordType === 'triage') return translate('Triage');
  if (recordType === 'measurements') return translate('Clinical measurements');
  return TYPE_LABELS[recordType] || recordType;
};

// A measurements record's day, a calendar date (toStoredDate's shape).
const measuredOnText = (record, dateFormat) => {
  const day = toLocalDate(record?.measurements?.measuredOn);
  return day ? moment(day).format(dateFormat) : '-';
};

// The summaries take the component's `t` (TGCLOUD-1893); English when omitted.
function getPrsSummary(prsResult, t = english) {
  if (!prsResult) return null;
  const parts = [];
  if (prsResult.zscore != null) parts.push(`z=${prsResult.zscore}`);
  if (prsResult.percentile != null)
    parts.push(
      t('{{percentile}}th percentile', { percentile: prsResult.percentile })
    );
  if (prsResult.relativeRisk != null)
    parts.push(t('RR={{value}}', { value: prsResult.relativeRisk }));
  return parts.length ? parts.join(' | ') : null;
}

function getCanriskSummary(canriskResult, t = english) {
  try {
    const pedigreeResult = canriskResult?.pedigree_result?.[0];
    const tenYr =
      pedigreeResult?.ten_yr_cancer_risk?.[0]?.['breast cancer risk']?.percent;
    const lifetime =
      pedigreeResult?.lifetime_cancer_risk?.[0]?.['breast cancer risk']
        ?.percent;
    if (tenYr != null && lifetime != null) {
      return t('10yr: {{tenYear}}% | Lifetime: {{lifetime}}%', {
        tenYear: tenYr,
        lifetime
      });
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

/**
 * A Gail record's race, as the Risk Factor form offers it. Stored as the
 * option's value ('white'); an older record may hold the label. Anything else
 * is shown as stored and never translated.
 */
export const gailRaceText = (race, t = english) => {
  const option = CONSTANTS.raceOptions.find(
    (o) => o.value === race || o.label === race
  );
  return option ? t(option.label) : race;
};

// `label` is this file's own English, translated here; `value` is shown as it
// is given, so a caller translates a word value itself.
function DetailRow({ label, value }) {
  const t = useAutoTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        py: 0.5
      }}
    >
      <Typography variant="body1" sx={{ mr: 2 }}>
        {t(label)}
      </Typography>
      <Typography variant="body1">{String(value)}</Typography>
    </Box>
  );
}

function GailDetail({ record }) {
  const t = useAutoTranslation();
  const rows = [
    ['Score', record.gailRiskScore],
    ['Age', record.age],
    ['Race', record.race != null ? gailRaceText(record.race, t) : null],
    ['Age at first menstruation', record.ageMen],
    ['Number of biopsies', record.nBiops],
    ['Age at first birth', record.ageFLB],
    ['1st degree relatives with breast cancer', record.numRel],
    [
      'Personal history of breast cancer / DCIS / LCIS',
      record.isBreastCancer != null
        ? record.isBreastCancer
          ? t('Yes')
          : t('No')
        : null
    ],
    [
      'Known BRCA1/BRCA2 mutation or hereditary syndrome',
      record.isMutation != null
        ? record.isMutation
          ? t('Yes')
          : t('No')
        : null
    ]
  ].filter(([, v]) => v != null);

  return (
    <Box>
      {rows.map(([k, v]) => (
        <DetailRow key={k} label={k} value={v} />
      ))}
    </Box>
  );
}

function CanRiskDetail({ record }) {
  const t = useAutoTranslation();

  const result = record.canriskResult;
  if (!result) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary'
        }}
      >
        {t('No scores calculated.')}
      </Typography>
    );
  }

  const pedigreeResult = result?.pedigree_result?.[0];
  const tenYrRisks = pedigreeResult?.ten_yr_cancer_risk?.[0] || {};
  const lifetimeRisks = pedigreeResult?.lifetime_cancer_risk?.[0] || {};
  const mutationProbs = pedigreeResult?.mutation_probabilties?.[0] || {};

  const riskRows = Object.keys(tenYrRisks).map((cancer) => ({
    cancer,
    tenYr: tenYrRisks[cancer]?.percent,
    lifetime: lifetimeRisks[cancer]?.percent
  }));

  const mutRows = Object.entries(mutationProbs);

  return (
    <Box>
      {riskRows.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            {t('Cancer Risk')}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 80px 80px',
              columnGap: 1,
              mb: 1
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 700
              }}
            >
              {t('Cancer')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 700,
                textAlign: 'right'
              }}
            >
              {t('10-year')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 700,
                textAlign: 'right'
              }}
            >
              {t('Lifetime')}
            </Typography>
            {riskRows.map(({ cancer, tenYr, lifetime }) => (
              <React.Fragment key={cancer}>
                {/* The model's own fixed names for each cancer, not record
                    data, so they are translated to show. */}
                <Typography
                  variant="body2"
                  sx={{ textTransform: 'capitalize' }}
                >
                  {t(cancer)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'right'
                  }}
                >
                  {tenYr != null ? `${tenYr}%` : '—'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'right'
                  }}
                >
                  {lifetime != null ? `${lifetime}%` : '—'}
                </Typography>
              </React.Fragment>
            ))}
          </Box>
        </>
      )}
      {mutRows.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" gutterBottom>
            {t('Mutation Probabilities')}
          </Typography>
          {mutRows.map(([gene, val]) => (
            <Box
              key={gene}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 0.25
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary'
                }}
              >
                {gene}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600
                }}
              >
                {val != null ? `${val.percent}%` : '—'}
              </Typography>
            </Box>
          ))}
        </>
      )}
      {result?.warnings?.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              color: 'warning.main'
            }}
          >
            {t('Warnings')}
          </Typography>
          {result.warnings.map((w, i) => (
            <Typography
              key={i}
              variant="body2"
              sx={{
                color: 'warning.main'
              }}
            >
              {w}
            </Typography>
          ))}
        </>
      )}
    </Box>
  );
}

function MeasurementsDetail({ record, dateFormat }) {
  const t = useAutoTranslation();
  const m = record.measurements || {};
  const bmi = bmiOf(m.heightCm, m.weightKg);
  const rows = [
    ['measuredOn', measuredOnText(record, dateFormat)],
    ['systolicBp', m.systolicBp],
    ['diastolicBp', m.diastolicBp],
    ['totalCholesterol', m.totalCholesterol],
    ['hdlCholesterol', m.hdlCholesterol],
    [
      'cholesterolHdlRatio',
      m.cholesterolHdlRatio != null
        ? m.ratioCalculated
          ? t('{{ratio}} (worked out from the cholesterol values)', {
              ratio: m.cholesterolHdlRatio
            })
          : m.cholesterolHdlRatio
        : null
    ],
    ['heightCm', m.heightCm],
    ['weightKg', m.weightKg],
    ['smokingStatus', m.smokingStatus ? smokingText(m.smokingStatus, t) : null],
    ['townsendScore', m.townsendScore]
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');
  return (
    <Box>
      {rows.map(([field, value]) => (
        <DetailRow
          key={field}
          // i18n-ignore: DetailRow translates its own label
          label={MEASUREMENT_LABELS[field]}
          value={value}
        />
      ))}
      {bmi !== null && (
        <DetailRow
          // i18n-ignore: DetailRow translates its own label
          label="BMI"
          value={bmi.toFixed(1)}
        />
      )}
      {m.source === 'calculation' && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('Entered in a cardiovascular risk calculation.')}
        </Typography>
      )}
    </Box>
  );
}

export function RecordDetailModal({ record, dateFormat, onClose, onDelete }) {
  const t = useAutoTranslation();

  if (!record) return null;

  const label = typeLabel(record, t);
  const date =
    record.recordType === 'measurements'
      ? measuredOnText(record, dateFormat)
      : record.createdAt
      ? moment(record.createdAt).format(dateFormat)
      : '-';

  const title = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}
    >
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
        {t('Risk Factor')}
      </Typography>
      <Typography sx={{ fontSize: '1.25rem' }}>- {date}</Typography>
      <Chip
        label={label}
        size="small"
        sx={{ backgroundColor: '#2BA9BF', color: '#fff', fontWeight: 700 }}
      />
    </Box>
  );

  return (
    <CustomDialog
      open
      title={title}
      handleClose={onClose}
      allowBackgroundClose
      maxWidth="sm"
      minHeight="auto"
      onClose
      showActions
      saveBtnLabel="Delete"
      onSave={onDelete}
      cancelBtnLabel="Close"
    >
      {record.recordType === 'gail' && <GailDetail record={record} />}
      {record.recordType === 'canrisk' && <CanRiskDetail record={record} />}
      {record.recordType === 'prs' && <PrsDetail record={record} />}
      {record.recordType === 'triage' && (
        <PackResult result={fromStored(record.triageResult)} stored />
      )}
      {record.recordType === 'measurements' && (
        <MeasurementsDetail record={record} dateFormat={dateFormat} />
      )}
      {record.recordType === 'qrisk3' && (
        <Qrisk3StoredResult result={record.qrisk3Result} />
      )}
    </CustomDialog>
  );
}

function PrsDetail({ record }) {
  const t = useAutoTranslation();

  const result = record.prsResult;
  if (!result) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('No score recorded.')}
      </Typography>
    );
  }

  const rows = [
    ['PGS Catalog ID', result.pgsId],
    ['Model', result.pgsName],
    ['Trait', result.trait],
    ['Ancestry', result.ancestry],
    ['Z-score', result.zscore],
    ['Percentile', result.percentile != null ? `${result.percentile}%` : null],
    ['Relative risk / odds ratio', result.relativeRisk],
    [
      'Source',
      t(
        CONSTANTS.prsSourceOptions.find((o) => o.value === result.source)
          ?.label || 'Manually entered'
      )
    ],
    // Only present for records from the "Compute from VCF" proof-of-concept
    // — a raw, unnormalised score, distinct from the z-score/percentile above.
    ['Raw score (unnormalised)', result.rawScore],
    [
      'Variant coverage',
      result.variantCoveragePct != null ? `${result.variantCoveragePct}%` : null
    ],
    ['Notes', result.notes]
  ].filter(([, v]) => v != null && v !== '');

  return (
    <Box>
      {rows.map(([k, v]) => (
        <DetailRow key={k} label={k} value={v} />
      ))}
      {result.publication?.title && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" gutterBottom>
            {t('Publication')}
          </Typography>
          <Typography variant="body2">{result.publication.title}</Typography>
          {result.publication.pmid && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('PMID')} {result.publication.pmid}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

function DeleteConfirmModal({ record, dateFormat, onConfirm, onCancel }) {
  const t = useAutoTranslation();

  if (!record) return null;

  const label = typeLabel(record, t);
  const date = record.createdAt
    ? moment(record.createdAt).format(dateFormat)
    : '—';

  return (
    <CustomDialog
      open
      title="Delete Risk Record"
      titleAppearance="warning"
      handleClose={onCancel}
      allowBackgroundClose
      maxWidth="xs"
      minHeight="auto"
      onClose
      showActions
      saveBtnLabel="Delete"
      onSave={onConfirm}
      cancelBtnLabel="Cancel"
    >
      <Typography>
        {t('Are you sure you want to delete the')} <strong>{label}</strong>{' '}
        {t('record from')} <strong>{date}</strong>
        {t('? This cannot be undone.')}
      </Typography>
    </CustomDialog>
  );
}

function RecordTile({ record, isLatest, dateFormat, onClick }) {
  const t = useAutoTranslation();

  const colour = TYPE_COLOURS[record.recordType] || '#9b9b9b';
  const label = typeLabel(record, t);
  const date =
    record.recordType === 'measurements'
      ? measuredOnText(record, dateFormat)
      : record.createdAt
      ? moment(record.createdAt).format(dateFormat)
      : '-';

  const canriskSummary =
    record.recordType === 'canrisk'
      ? getCanriskSummary(record.canriskResult, t)
      : null;
  const prsSummary =
    record.recordType === 'prs' ? getPrsSummary(record.prsResult, t) : null;

  return (
    <Box
      onClick={onClick}
      sx={{
        border: '1px solid #e0e0e0',
        borderLeft: `3px solid ${colour}`,
        borderRadius: '6px',
        padding: '8px 10px',
        backgroundColor: '#fff',
        mb: 1,
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#fafafa' }
      }}
    >
      <Typography
        sx={{
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: colour,
          lineHeight: 1
        }}
      >
        {record.recordType === 'triage'
          ? t('Triage - {{label}}', { label })
          : record.recordType === 'measurements'
          ? label
          : t('Risk Score - {{label}}', { label })}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {record.recordType === 'gail' && record.gailRiskScore != null && (
          <>
            {t('Risk score:')} {record.gailRiskScore}
          </>
        )}
        {record.recordType === 'canrisk' && (
          <>
            {canriskSummary ||
              (record.canriskResult
                ? t('Scores calculated')
                : t('Pedigree downloaded'))}
          </>
        )}
        {record.recordType === 'prs' && (
          <>
            {record.prsResult?.trait || record.prsResult?.pgsId}
            {prsSummary ? ` — ${prsSummary}` : ''}
          </>
        )}
        {record.recordType === 'triage' &&
          t('Version {{version}}: {{outcome}}', {
            version: record.triageResult?.packVersion || '',
            outcome: outcomeText(record.triageResult?.outcome, t)
          })}
        {record.recordType === 'measurements' &&
          measurementSummary(record.measurements, t)}
        {record.recordType === 'qrisk3' &&
          qrisk3Summary(record.qrisk3Result, t)}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25 }}>
        {date}
      </Typography>
      {isLatest && (
        <Typography
          variant="body2"
          sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 0.25 }}
        >
          {t('Latest result')}
        </Typography>
      )}
    </Box>
  );
}

// A record counts as "scored" once a calculation has produced a result (or,
// for PRS, once a score has been entered — there's no in-app calculation).
const isScored = (r) =>
  (r.recordType === 'gail' && r.gailRiskScore != null) ||
  (r.recordType === 'canrisk' && r.canriskResult != null) ||
  (r.recordType === 'prs' &&
    (r.prsResult?.zscore != null || r.prsResult?.percentile != null)) ||
  (r.recordType === 'triage' && r.triageResult != null) ||
  (r.recordType === 'measurements' && r.measurements != null) ||
  (r.recordType === 'qrisk3' && r.qrisk3Result != null);

export default function RiskRecordsList({
  userId,
  refreshKey,
  recordType,
  memberIds,
  scoredOnly = false,
  // TGCLOUD-1669 — (recordType, facts) => void. Lets RiskFactorsMenu build the
  // collapsed-row summary from records this list already fetched, without a
  // second request and without moving the fetch.
  onFacts
}) {
  const { theme } = React.useContext(ThemeContext);
  const dateFormat = theme?.dateFormat || 'DD/MM/YYYY';

  const [records, setRecords] = React.useState([]);
  // `records` starts empty and the fetch is async, so an unqualified count of 0
  // would mean "none recorded" for as long as the request was in flight. This
  // separates "not answered yet" from "answered, and the answer is none".
  const [loaded, setLoaded] = React.useState(false);
  const [selectedRecord, setSelectedRecord] = React.useState(null);
  const [recordToDelete, setRecordToDelete] = React.useState(null);

  const memberKey = (memberIds || []).filter(Boolean).map(String).join('|');

  React.useEffect(() => {
    if (!userId) return;
    // Back to pending for THIS fetch. `loaded` used to latch true forever, and
    // the report effect below depends on it, so when the person changed and the
    // new count happened to equal the old one (two patients with no risk
    // records, which is the common case) nothing in its dep array moved. The
    // list never re-reported, the parent never re-aggregated, and because
    // useSectionFacts wipes the facts on a person change, the RISK FACTORS
    // summary line simply stopped appearing.
    setLoaded(false);
    const ids = memberKey ? memberKey.split('|') : [];
    axios
      .get(APICONSTANTS.getRiskRecords.replace(':user_id', userId))
      .then(({ data }) => {
        const all = Array.isArray(data) ? data : [];
        setRecords(
          all.filter(
            (r) =>
              (!recordType || r.recordType === recordType) &&
              (ids.length === 0 || ids.includes(String(r.familyMemberId))) &&
              (!scoredOnly || isScored(r))
          )
        );
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [userId, refreshKey, recordType, memberKey, scoredOnly]);

  // Reported BEFORE the empty early-return below, so a list with no records
  // still tells the parent it has nothing. Placing it after that return would
  // break the rules of hooks and would also silence exactly the case the
  // summary needs to report.
  React.useEffect(() => {
    if (!onFacts) return;
    onFacts(recordType, {
      status: loaded ? 'ready' : 'pending',
      // Read when reported: a `t` here would re-run this on every translation.
      label: listLabel(recordType),
      count: records.length
    });
  }, [onFacts, recordType, loaded, records.length]);

  const handleDelete = () => {
    return axios
      .delete(APICONSTANTS.deleteRiskRecord.replace(':id', recordToDelete._id))
      .then(() => {
        setRecords((prev) => prev.filter((r) => r._id !== recordToDelete._id));
        setRecordToDelete(null);
      });
  };

  if (records.length === 0) return null;

  return (
    <>
      <Box
        sx={{
          mt: 1
        }}
      >
        {records.map((record, index) => (
          <RecordTile
            key={record._id}
            record={record}
            isLatest={index === 0}
            dateFormat={dateFormat}
            onClick={() => setSelectedRecord(record)}
          />
        ))}
      </Box>
      {selectedRecord && !recordToDelete && (
        <RecordDetailModal
          record={selectedRecord}
          dateFormat={dateFormat}
          onClose={() => setSelectedRecord(null)}
          // CustomDialog's Save handler calls `.then` on what onSave returns:
          // returning nothing threw a TypeError on every Delete press.
          onDelete={() => {
            setRecordToDelete(selectedRecord);
            return Promise.resolve();
          }}
        />
      )}
      {recordToDelete && (
        <DeleteConfirmModal
          record={recordToDelete}
          dateFormat={dateFormat}
          onConfirm={handleDelete}
          onCancel={() => setRecordToDelete(null)}
        />
      )}
    </>
  );
}
